/**
 * Go High Level (GHL) Webhook Integration
 * Sends form submissions to GHL CRM via webhook
 */

import { getUTMParams, type UTMParams } from './utm-tracker';

const GHL_WEBHOOK_URL = "https://services.leadconnectorhq.com/hooks/TKcORMZ4obsYAS2UwN8I/webhook-trigger/0b5d91f2-d674-41f9-a062-f00f6d9196b9";

// Mapping for service types (technical value -> display name)
const SERVICE_TYPE_LABELS: Record<string, string> = {
  'deep': 'Deep Cleaning',
  'regular': 'Regular House Cleaning',
  'one-time': 'One Time Cleaning',
  'move-in': 'Move In Cleaning',
  'move-out': 'Move Out Cleaning',
  'spring': 'Spring Cleaning',
  'holiday': 'Holiday Cleaning',
  'post-construction': 'Post-Construction Cleaning',
  'post-renovation': 'Post Renovation Cleaning',
  'airbnb': 'Airbnb Cleaning',
};

// Mapping for frequency (technical value -> display name)
const FREQUENCY_LABELS: Record<string, string> = {
  'onetime': 'One-time',
  'one-time': 'One-time',
  'weekly': 'Weekly',
  'biweekly': 'Bi-weekly',
  'bi-weekly': 'Bi-weekly',
  'monthly': 'Monthly',
};

// Mapping for preferred contact (remove emojis)
const CONTACT_METHOD_LABELS: Record<string, string> = {
  'phone': 'Phone Call',
  'text': 'Text Message',
  'email': 'Email',
};

// Mapping for property type
const PROPERTY_TYPE_LABELS: Record<string, string> = {
  'house': 'House',
  'apartment': 'Apartment',
  'condo': 'Condo',
  'office': 'Office',
  'retail': 'Retail',
  'other': 'Other Commercial',
};

// Mapping for urgency/when do you need service
const URGENCY_LABELS: Record<string, string> = {
  'asap': 'As soon as possible',
  'this-week': 'This week',
  'next-week': 'Next week',
  'flexible': "I'm flexible",
};

// Helper functions to map values
const mapServiceType = (value?: string): string | undefined => {
  if (!value) return undefined;
  return SERVICE_TYPE_LABELS[value] || value;
};

const mapFrequency = (value?: string): string | undefined => {
  if (!value) return undefined;
  return FREQUENCY_LABELS[value] || value;
};

const mapContactMethod = (value?: string): string | undefined => {
  if (!value) return undefined;
  return CONTACT_METHOD_LABELS[value] || value;
};

const mapPropertyType = (value?: string): string | undefined => {
  if (!value) return undefined;
  return PROPERTY_TYPE_LABELS[value] || value;
};

const mapUrgency = (value?: string): string | undefined => {
  if (!value) return undefined;
  return URGENCY_LABELS[value] || value;
};

export interface GHLFormData {
  // Required fields
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  
  // Form metadata
  formType: 'contact' | 'estimate' | 'quick-estimate';
  pageUrl: string;
  timestamp: string;
  
  // Address fields (separated)
  addressLine1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  
  // Optional fields
  message?: string;
  propertyType?: string;
  squareFootage?: number;
  bedrooms?: number;
  bathrooms?: number;
  serviceType?: string;
  frequency?: string;
  preferredContact?: string;
  urgency?: string;
  additionalServices?: string[];
  
  // UTM Parameters (added automatically)
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
}

/**
 * Sends form data to GHL webhook
 * Automatically includes UTM parameters if available
 * Does not throw errors - logs them instead to avoid blocking form submission
 */
export async function sendToGHL(formData: GHLFormData): Promise<boolean> {
  try {
    // Get stored UTM parameters
    const utmParams = getUTMParams();
    
    // Prepare mapped data
    const payload = {
      // Contact info (unchanged)
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      
      // Address fields (separated, not concatenated)
      addressLine1: formData.addressLine1,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      
      // Mapped values (technical -> display names)
      propertyType: mapPropertyType(formData.propertyType),
      serviceType: mapServiceType(formData.serviceType),
      frequency: mapFrequency(formData.frequency),
      preferredContact: mapContactMethod(formData.preferredContact),
      urgency: mapUrgency(formData.urgency),
      
      // Other optional fields
      message: formData.message,
      squareFootage: formData.squareFootage,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      additionalServices: formData.additionalServices,
      
      // Metadata
      formType: formData.formType,
      pageUrl: formData.pageUrl,
      timestamp: formData.timestamp,
      
      // UTM Parameters
      ...utmParams,
    };

    console.log('Sending data to GHL:', payload);

    const response = await fetch(GHL_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`GHL webhook failed: ${response.status} ${response.statusText}`);
    }

    console.log('Successfully sent data to GHL');
    return true;
  } catch (error) {
    // Log error but don't throw - we don't want to block the user
    console.error('Error sending data to GHL webhook:', error);
    return false;
  }
}
